/**
 * GroupBookingNew (iter130) — Unified session request form.
 *
 * Replaces the iter126 category-branched form. Same UI for Solo / Group /
 * Club now that the chooser is gone: pick 1..N players, up to admin-cap
 * coaches, exact time or date range, invite by name/email, split-pay or
 * organiser-pays. Cascade + broadcast toggles surface as soon as 2+ coaches
 * are picked.
 *
 * Coach cap is admin-configurable via /api/features.max_coaches_per_request
 * (default 2). Fetched on mount; falls back to 2 if the network call fails.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { X, ChevronUp, ChevronDown, Radio, ListOrdered } from "lucide-react";
import api from "@/lib/api";
import InvitePlayersPicker from "@/components/InvitePlayersPicker";
import { fetchCoachDayStatus, requestStatusFromDays, STATUS_META } from "@/components/CoachAvailabilityCalendar";

const FOCUS = [
  { v: "doubles_strategy", l: "Doubles Strategy" },
  { v: "footwork", l: "Footwork" },
  { v: "serves", l: "Serves" },
  { v: "movement", l: "Movement" },
  { v: "match_play", l: "Match Play" },
  { v: "fitness", l: "Fitness" },
  { v: "beginner_fundamentals", l: "Beginner Fundamentals" },
];

const TIME_PRESETS = [
  { v: "",              l: "Any time" },
  { v: "06:00-12:00",   l: "Mornings (6-12)" },
  { v: "12:00-17:00",   l: "Afternoons (12-17)" },
  { v: "17:00-21:00",   l: "Evenings (17-21)" },
];

// Iter132 — Category-driven form config. Solo strips invitees + payment
// split + cascade UI; Group opens invitees + split but keeps cascade
// available; Club is the full advanced experience (max cap from admin
// settings + open slots + broadcast).
const CATEGORY_CONFIG = {
  solo:  {
    eyebrow: "SOLO SESSION",
    title: "Just you and a coach.",
    subtitle: "1:1 focus — pick a coach and propose a time.",
    defaultMax: 1, minMax: 1, maxMax: 1,
    showInvitees: false, showPaymentMode: false, showOpenSlots: false,
  },
  group: {
    eyebrow: "GROUP SESSION",
    title: "Rally up to 6 friends.",
    subtitle: "Invite mates, split the bill or pay for everyone.",
    defaultMax: 4, minMax: 2, maxMax: 6,
    showInvitees: true, showPaymentMode: true, showOpenSlots: false,
  },
  club:  {
    eyebrow: "CLUB REQUEST",
    title: "Coaching for your club.",
    subtitle: "Up to 8 players. Broadcast to your top coaches, first to accept wins.",
    defaultMax: 6, minMax: 2, maxMax: 8,
    showInvitees: true, showPaymentMode: true, showOpenSlots: true,
  },
};

const BLOCKED_MSG = "This coach is not available on the selected date. Please choose a different date or coach.";

export default function GroupBookingNew() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const type = (search.get("type") || "club").toLowerCase();
  const cfg = CATEGORY_CONFIG[type] || CATEGORY_CONFIG.club;

  const [coaches, setCoaches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scheduleMode, setScheduleMode] = useState("range");
  const [customWindow, setCustomWindow] = useState(false);
  const [maxCoaches, setMaxCoaches] = useState(2);
  const [featCaps, setFeatCaps] = useState({ global: 2, solo: null, group: null, club: null });
  const [form, setForm] = useState({
    sport: "pickleball",
    coach_priority_ids: [],
    cascade_broadcast: false,
    venue_name: "",
    start: "",
    date_range_start: "",
    date_range_end: "",
    preferred_time_window: "",
    duration_min: 60,
    max_players: cfg.defaultMax,
    payment_mode: cfg.showPaymentMode ? "split_equally" : "organiser_pays_all",
    invited_player_ids: [], invited_emails: [], open_slots: 0, focus_areas: [], notes: "",
  });

  // Iter151 — Prefill from URL params so the "Book Focus Session" button on
  // the player profile can drop a coach into a targeted request with the
  // relevant focus areas already selected. Focus areas are comma-separated.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const focusParam = q.get("focus");
    const coachParam = q.get("coach");
    if (!focusParam && !coachParam) return;
    setForm((f) => {
      const next = { ...f };
      if (focusParam) {
        const raw = focusParam.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
        // Allow up to 2 focus areas — same rule the group booking form enforces.
        next.focus_areas = Array.from(new Set(raw)).slice(0, 2);
      }
      if (coachParam) {
        next.coach_priority_ids = [coachParam];
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/public/coaches").then((r) => setCoaches(r.data || [])).catch(() => {});
    // Read live admin cap. Silent-failure: default state (2) already sane.
    api.get("/features").then((r) => {
      const cap = Number(r.data?.max_coaches_per_request);
      if (Number.isInteger(cap) && cap >= 1 && cap <= 8) setMaxCoaches(cap);
      setFeatCaps({
        global: Number.isInteger(cap) ? cap : 2,
        solo: r.data?.max_coaches_solo ?? null,
        group: r.data?.max_coaches_group ?? null,
        club: r.data?.max_coaches_club ?? null,
      });
    }).catch(() => {});
  }, []);

  // Iter131 — Recompute the effective cap whenever the organiser's request
  // shape shifts category (max_players / broadcast). Bumping max_players
  // from 4 → 8 while cap_group=2 vs cap_club=3 should visibly change the
  // coach counter without a page reload.
  // Iter132 — Category is now driven by the ?type= query param when
  // present (Solo/Group cards route with a type; Club form defaults to
  // 'club'). Fall back to shape-derivation for legacy links.
  useEffect(() => {
    let category;
    if (["solo", "group", "club"].includes(type)) {
      category = type;
    } else {
      category = form.max_players <= 1
        ? "solo"
        : (form.max_players >= 7 || form.cascade_broadcast) ? "club" : "group";
    }
    // Iter132 — Solo is a strict 1-coach flow regardless of admin caps.
    // Group + Club read the per-category cap (falls back to global).
    if (category === "solo") {
      setMaxCoaches(1);
    } else {
      const eff = featCaps[category] ?? featCaps.global ?? 2;
      setMaxCoaches(Math.max(1, Math.min(8, Number(eff) || 2)));
    }
  }, [featCaps, form.max_players, form.cascade_broadcast, type]);

  // Iter132 — Reset per-type defaults on type switch so max_players etc.
  // reflect the picked category rather than lingering from a previous mode.
  useEffect(() => {
    setForm((f) => ({
      ...f,
      max_players: cfg.defaultMax,
      payment_mode: cfg.showPaymentMode ? (f.payment_mode || "split_equally") : "organiser_pays_all",
      invited_player_ids: cfg.showInvitees ? f.invited_player_ids : [],
      invited_emails: cfg.showInvitees ? f.invited_emails : [],
      open_slots: cfg.showOpenSlots ? f.open_slots : 0,
      cascade_broadcast: type === "club" ? f.cascade_broadcast : false,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const coachById = useMemo(() => new Map(coaches.map((c) => [c.id, c])), [coaches]);
  const [coachAvail, setCoachAvail] = useState({});
  const reqStart = scheduleMode === "exact" ? (form.start || "").slice(0, 10) : form.date_range_start;
  const reqEnd = scheduleMode === "exact" ? (form.start || "").slice(0, 10) : form.date_range_end;
  useEffect(() => {
    if (!reqStart || !reqEnd || reqEnd < reqStart) { setCoachAvail({}); return; }
    let live = true;
    Promise.all(form.coach_priority_ids.map((cid) => fetchCoachDayStatus(cid, reqStart, reqEnd)
      .then((days) => [cid, requestStatusFromDays(days)]).catch(() => [cid, "negotiable"])))
      .then((pairs) => { if (live) setCoachAvail(Object.fromEntries(pairs)); });
    return () => { live = false; };
  }, [form.coach_priority_ids, reqStart, reqEnd]);
  const pickedStatuses = form.coach_priority_ids.map((cid) => coachAvail[cid]).filter(Boolean);
  const allBlocked = pickedStatuses.length > 0 && pickedStatuses.length === form.coach_priority_ids.length
    && pickedStatuses.every((st) => st === "blocked");
  const blockedNames = form.coach_priority_ids.filter((cid) => coachAvail[cid] === "blocked").map((cid) => coachById.get(cid)?.name || "Coach");
  const anyNegotiable = form.coach_priority_ids.some((cid) => coachAvail[cid] === "negotiable");
  const availableCoaches = useMemo(
    () => coaches.filter((c) => !form.coach_priority_ids.includes(c.id)),
    [coaches, form.coach_priority_ids],
  );

  const addCoach = (id) => {
    if (!id) return;
    if (form.coach_priority_ids.length >= maxCoaches) {
      toast.error(`Max ${maxCoaches} coach${maxCoaches !== 1 ? "es" : ""} — remove one first (admin can raise the cap).`);
      return;
    }
    setForm((f) => ({ ...f, coach_priority_ids: [...f.coach_priority_ids, id] }));
  };
  const removeCoach = (id) => setForm((f) => ({
    ...f, coach_priority_ids: f.coach_priority_ids.filter((x) => x !== id),
  }));
  const moveCoach = (idx, delta) => {
    const list = [...form.coach_priority_ids];
    const j = idx + delta;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    setForm((f) => ({ ...f, coach_priority_ids: list }));
  };

  const toggleFocus = (v) => setForm((f) => ({
    ...f,
    focus_areas: f.focus_areas.includes(v)
      ? f.focus_areas.filter((x) => x !== v)
      : f.focus_areas.length < 2 ? [...f.focus_areas, v] : f.focus_areas,
  }));

  const submit = async () => {
    if (form.coach_priority_ids.length === 0) { toast.error("Pick at least one coach"); return; }
    if (!form.venue_name) { toast.error("Enter a venue"); return; }
    if (scheduleMode === "exact" && !form.start) { toast.error("Pick a start time"); return; }
    if (scheduleMode === "range" && (!form.date_range_start || !form.date_range_end)) {
      toast.error("Pick a date range"); return;
    }
    if (form.focus_areas.length === 0) { toast.error("Pick at least one focus area"); return; }
    if (allBlocked) { toast.error(BLOCKED_MSG); return; }

    const payload = {
      ...form,
      coach_id: form.coach_priority_ids[0],
      start: scheduleMode === "exact" ? form.start : null,
      date_range_start: scheduleMode === "range" ? form.date_range_start : null,
      date_range_end: scheduleMode === "range" ? form.date_range_end : null,
      preferred_time_window: scheduleMode === "range" ? (form.preferred_time_window || null) : null,
    };

    setBusy(true);
    try {
      const { data } = await api.post("/group-bookings", payload);
      const coachCount = form.coach_priority_ids.length;
      toast.success(
        form.cascade_broadcast && coachCount > 1
          ? `Sent to ${coachCount} coaches — first to approve wins`
          : "Sent — awaiting coach approval",
      );
      if (data.skipped_coaches?.length) {
        toast.warning(`${data.skipped_coaches.map((c) => c.name).join(", ")} skipped — not available on the selected date.`, { duration: 8000 });
      }
      nav(`/group-bookings/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 py-6" data-testid="group-booking-new">
      <div>
        <div className="label-mono text-[#CCFF00]">{cfg.eyebrow}</div>
        <h1 className="h-display text-4xl mt-1">{cfg.title}</h1>
        <p className="text-sm text-[#A3A3A3] mt-2">{cfg.subtitle}</p>
      </div>

      <div className="card-surface p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs uppercase tracking-wider text-[#A3A3A3]">Sport
            <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })}
              className="mt-1 w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white" data-testid="gb-sport">
              <option value="pickleball">Pickleball</option>
              <option value="squash">Squash</option>
            </select>
          </label>
          <label className="text-xs uppercase tracking-wider text-[#A3A3A3]">Venue
            <input value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
              placeholder="e.g. Riverside Sports Centre"
              className="mt-1 w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white" data-testid="gb-venue" />
          </label>
        </div>

        <div data-testid="gb-coach-picker">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-[#A3A3A3]">
              {maxCoaches === 1 ? "Coach" : `Coaches — pick up to ${maxCoaches} in priority order`}
            </div>
            <div className="text-[10px] font-mono text-[#525252]">{form.coach_priority_ids.length}/{maxCoaches}</div>
          </div>

          {form.coach_priority_ids.length > 0 && (
            <div className="space-y-2 mb-3" data-testid="gb-coach-priority-list">
              {form.coach_priority_ids.map((id, idx) => {
                const c = coachById.get(id);
                return (
                  <div key={id} className="flex items-center gap-2 border border-[#CCFF00] bg-[#CCFF0010] px-3 py-2" data-testid={`gb-coach-priority-${idx}`}>
                    <span className="w-6 h-6 border border-[#CCFF00] text-[#CCFF00] font-mono text-xs inline-flex items-center justify-center font-bold shrink-0">{idx + 1}</span>
                    <span className="flex-1 text-sm text-white truncate">{c?.name || id}</span>
                    {coachAvail[id] && (
                      <span data-testid={`gb-coach-avail-${idx}`} data-status={coachAvail[id]}
                        className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border shrink-0 ${STATUS_META[coachAvail[id]].cls}`}>
                        {STATUS_META[coachAvail[id]].label}
                      </span>
                    )}
                    <button type="button" onClick={() => moveCoach(idx, -1)} disabled={idx === 0}
                      data-testid={`gb-coach-up-${idx}`}
                      className="p-1 text-[#A3A3A3] hover:text-white disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                    <button type="button" onClick={() => moveCoach(idx, 1)} disabled={idx === form.coach_priority_ids.length - 1}
                      data-testid={`gb-coach-down-${idx}`}
                      className="p-1 text-[#A3A3A3] hover:text-white disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                    <button type="button" onClick={() => removeCoach(id)}
                      data-testid={`gb-coach-remove-${idx}`}
                      className="p-1 text-[#A3A3A3] hover:text-[#FF4500]"><X className="w-4 h-4" /></button>
                  </div>
                );
              })}
            </div>
          )}

          {form.coach_priority_ids.length < maxCoaches && (
            <select onChange={(e) => { addCoach(e.target.value); e.target.value = ""; }} defaultValue=""
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white" data-testid="gb-coach-add">
              <option value="">— add a coach —</option>
              {availableCoaches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {form.coach_priority_ids.length >= maxCoaches && (
            <div
              data-testid="gb-coach-cap-hint"
              className="mt-2 flex items-center gap-2 px-3 py-2 border border-[#FF9F1C] bg-[#FF9F1C10] text-[11px] text-white"
            >
              <span className="text-[#FF9F1C] font-semibold uppercase tracking-wider text-[10px]">Cap reached</span>
              <span className="text-[#A3A3A3]">You've hit the {maxCoaches}-coach limit for this request. Ask an admin to raise the cap in Feature Settings.</span>
            </div>
          )}

          {form.coach_priority_ids.length > 1 && (
            <div className="mt-3 border border-[#262626] p-3 space-y-2" data-testid="gb-cascade-toggle">
              <button type="button" onClick={() => setForm((f) => ({ ...f, cascade_broadcast: false }))}
                data-testid="gb-cascade-priority"
                className="w-full flex items-start gap-3 text-left p-2 transition-colors"
                style={!form.cascade_broadcast ? { background: "#CCFF0010", border: "1px solid #CCFF00" } : { border: "1px solid transparent" }}>
                <ListOrdered className="w-4 h-4 text-[#CCFF00] mt-0.5 shrink-0" />
                <span>
                  <span className="block text-sm text-white font-semibold">Priority cascade <span className="text-[10px] text-[#CCFF00] uppercase tracking-wider">recommended</span></span>
                  <span className="block text-xs text-[#A3A3A3] mt-0.5">Coach #1 gets the request first. If no reply in 12h, cascades to #2 (then #3).</span>
                </span>
              </button>
              <button type="button" onClick={() => setForm((f) => ({ ...f, cascade_broadcast: true }))}
                data-testid="gb-cascade-broadcast"
                className="w-full flex items-start gap-3 text-left p-2 transition-colors"
                style={form.cascade_broadcast ? { background: "#FF450010", border: "1px solid #FF4500" } : { border: "1px solid transparent" }}>
                <Radio className="w-4 h-4 text-[#FF4500] mt-0.5 shrink-0" />
                <span>
                  <span className="block text-sm text-white font-semibold">Broadcast to all</span>
                  <span className="block text-xs text-[#A3A3A3] mt-0.5">Every coach on your list is notified now — first to approve wins.</span>
                </span>
              </button>
            </div>
          )}
        </div>

        <div data-testid="gb-schedule">
          <div className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-2">When</div>
          <div className="flex gap-2 mb-3">
            <button type="button" onClick={() => setScheduleMode("range")}
              data-testid="gb-schedule-range"
              className="flex-1 px-3 py-2 text-xs uppercase tracking-wider border"
              style={scheduleMode === "range"
                ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00" }
                : { borderColor: "#262626", color: "#A3A3A3" }}>
              Propose a range
            </button>
            <button type="button" onClick={() => setScheduleMode("exact")}
              data-testid="gb-schedule-exact"
              className="flex-1 px-3 py-2 text-xs uppercase tracking-wider border"
              style={scheduleMode === "exact"
                ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00" }
                : { borderColor: "#262626", color: "#A3A3A3" }}>
              Exact start time
            </button>
          </div>

          {scheduleMode === "exact" ? (
            <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block">Start
              <input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })}
                className="mt-1 w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white" data-testid="gb-start" />
            </label>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block">From
                  <input type="date" value={form.date_range_start}
                    onChange={(e) => setForm({ ...form, date_range_start: e.target.value })}
                    className="mt-1 w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white" data-testid="gb-range-start" />
                </label>
                <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] block">To
                  <input type="date" value={form.date_range_end}
                    onChange={(e) => setForm({ ...form, date_range_end: e.target.value })}
                    className="mt-1 w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white" data-testid="gb-range-end" />
                </label>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1">Time window (coach will pick the exact slot)</div>
                <div className="flex flex-wrap gap-1.5">
                  {TIME_PRESETS.map((t) => (
                    <button type="button" key={t.v || "any"}
                      onClick={() => { setForm((f) => ({ ...f, preferred_time_window: t.v })); setCustomWindow(false); }}
                      data-testid={`gb-time-preset-${t.v || "any"}`}
                      className="px-2.5 py-1 text-[11px] uppercase tracking-wider border"
                      style={!customWindow && form.preferred_time_window === t.v
                        ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00" }
                        : { borderColor: "#262626", color: "#A3A3A3" }}>
                      {t.l}
                    </button>
                  ))}
                  <button type="button" onClick={() => setCustomWindow(true)}
                    data-testid="gb-time-preset-custom"
                    className="px-2.5 py-1 text-[11px] uppercase tracking-wider border"
                    style={customWindow ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00" } : { borderColor: "#262626", color: "#A3A3A3" }}>
                    Custom
                  </button>
                </div>
                {customWindow && (
                  <input type="text" placeholder="e.g. 19:00-21:30" value={form.preferred_time_window}
                    onChange={(e) => setForm({ ...form, preferred_time_window: e.target.value })}
                    className="mt-2 w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white text-sm"
                    data-testid="gb-time-custom" />
                )}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs uppercase tracking-wider text-[#A3A3A3]">Duration (min)
            <input type="number" min={30} max={240} step={15} value={form.duration_min}
              onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })}
              className="mt-1 w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white" data-testid="gb-duration" />
          </label>
          {cfg.minMax !== cfg.maxMax && (
            <label className="text-xs uppercase tracking-wider text-[#A3A3A3]">Max players ({cfg.minMax}-{cfg.maxMax})
              <input type="number" min={cfg.minMax} max={cfg.maxMax} value={form.max_players}
                onChange={(e) => setForm({ ...form, max_players: Math.max(cfg.minMax, Math.min(cfg.maxMax, Number(e.target.value))) })}
                className="mt-1 w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white" data-testid="gb-max" />
            </label>
          )}
        </div>

        {cfg.showPaymentMode && (
          <div>
            <div className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-2">Payment mode</div>
            <div className="flex gap-2">
              {[["split_equally", "Split equally"], ["organiser_pays_all", "I pay all"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, payment_mode: v })}
                  data-testid={`gb-mode-${v}`}
                  className={`px-3 py-2 text-xs uppercase tracking-wider border ${form.payment_mode === v ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-[#262626] text-[#A3A3A3]"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-2">Focus (pick 1-2)</div>
          <div className="flex flex-wrap gap-2">
            {FOCUS.map((f) => (
              <button key={f.v} type="button" onClick={() => toggleFocus(f.v)}
                data-testid={`gb-focus-${f.v}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${form.focus_areas.includes(f.v) ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-[#262626] text-[#A3A3A3]"}`}>
                {f.l}
              </button>
            ))}
          </div>
        </div>

        {cfg.showInvitees && form.max_players > 1 && (
          <InvitePlayersPicker
            selectedIds={form.invited_player_ids}
            onIdsChange={(ids) => setForm((f) => ({ ...f, invited_player_ids: ids }))}
            selectedEmails={form.invited_emails}
            onEmailsChange={(ems) => setForm((f) => ({ ...f, invited_emails: ems }))}
            maxPlayers={form.max_players}
          />
        )}

        <div className="border-l-2 border-[#FF9F1C] bg-[#141414] px-3 py-2 text-[11px] text-[#A3A3A3]" data-testid="gb-conditions">
          <span className="text-white font-semibold uppercase tracking-wider">Booking conditions:</span> if you cancel a confirmed session after any player has paid by card, a <span className="text-[#FF9F1C]">10% administration fee</span> is deducted from that refund. Credit-paid shares are fully restored.
        </div>

        {allBlocked ? (
          <div className="border-l-2 border-[#FF4D4D] bg-[#2A0A0A] px-3 py-2 text-sm text-[#FF8080]" data-testid="gb-avail-blocked">
            {BLOCKED_MSG}
          </div>
        ) : (
          <>
            {blockedNames.length > 0 && (
              <div className="border-l-2 border-[#FF9F1C] bg-[#141414] px-3 py-2 text-[12px] text-[#E0E0E0]" data-testid="gb-avail-skipped">
                {blockedNames.join(", ")} {blockedNames.length === 1 ? "is" : "are"} not available on the selected date and will be skipped — your request goes to the remaining coach{form.coach_priority_ids.length - blockedNames.length === 1 ? "" : "es"}.
              </div>
            )}
            {anyNegotiable && (
              <div className="border-l-2 border-[#FFD500] bg-[#141414] px-3 py-2 text-[12px] text-[#FFE680]" data-testid="gb-avail-negotiable">
                This date is not confirmed as available — your request will be subject to coach confirmation.
              </div>
            )}
          </>
        )}

        <button type="button" onClick={submit} disabled={busy || allBlocked}
          data-testid="gb-submit"
          className="px-5 py-2 bg-[#CCFF00] text-black text-xs uppercase tracking-wider font-bold disabled:opacity-50">
          {busy ? "Sending…"
            : form.cascade_broadcast && form.coach_priority_ids.length > 1
              ? `Broadcast to ${form.coach_priority_ids.length} coaches`
              : "Send to coach"}
        </button>
      </div>
    </div>
  );
}
